/**
 * WordPress REST API Service
 * Connects React app to WordPress backend
 */

import axios from 'axios';

// WordPress API base URL
const WP_API_BASE = import.meta.env.VITE_WP_API_URL || 'http://localhost/wp-json/wp/v2';

// Create axios instance with default config
const wpApi = axios.create({
  baseURL: WP_API_BASE,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// WordPress API endpoints
export const wpEndpoints = {
  services: '/service',
  pages: '/pages',
  posts: '/posts',
  categories: '/categories',
  media: '/media',
  search: '/search',
};

/**
 * Fetch all services from WordPress
 */
export const fetchServices = async () => {
  try {
    // Use responseType: 'text' to get raw response and handle PHP output
    const response = await wpApi.get(wpEndpoints.services, {
      params: {
        per_page: 100,
        _embed: true, // Include featured images, author info, and terms (categories)
      },
      responseType: 'text' // Get raw text to handle PHP output before JSON
    });
    
    // Handle case where response.data is a string (might contain PHP output)
    let data = response.data;
    
    // If data is a string, try to extract JSON from it
    if (typeof data === 'string') {
      // Try to find JSON array in the string (handle PHP output before JSON)
      const jsonMatch = data.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        try {
          data = JSON.parse(jsonMatch[0]);
        } catch (e) {
          console.error('Failed to parse JSON from extracted match:', e);
          console.error('Response preview:', data.substring(0, 500));
          return [];
        }
      } else {
        // Try to parse the whole string as JSON
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error('Failed to parse JSON from string response:', e);
          console.error('Response preview:', data.substring(0, 500));
          return [];
        }
      }
    }
    
    // Ensure we return an array
    if (!Array.isArray(data)) {
      console.warn('Response is not an array:', typeof data, data);
      return [];
    }
    
    console.log(`✅ Successfully loaded ${data.length} services from WordPress API`);
    return data;
  } catch (error: any) {
    console.error('Error fetching services:', error);
    console.error('Error details:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      url: error?.config?.url || error?.request?.responseURL,
      code: error?.code
    });
    
    // Return empty array instead of throwing to prevent app crash
    return [];
  }
};

/**
 * Fetch a single service by ID or slug
 */
export const fetchService = async (idOrSlug: string | number) => {
  try {
    const response = await wpApi.get(`${wpEndpoints.services}/${idOrSlug}`, {
      params: {
        _embed: true,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching service:', error);
    throw error;
  }
};

/**
 * Fetch services by category
 */
export const fetchServicesByCategory = async (categoryId: number) => {
  try {
    const response = await wpApi.get(wpEndpoints.services, {
      params: {
        service_category: categoryId,
        per_page: 100,
        _embed: true,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching services by category:', error);
    throw error;
  }
};

/**
 * Fetch service categories
 */
export const fetchServiceCategories = async () => {
  try {
    const response = await wpApi.get('/service-category', {
      params: {
        per_page: 100,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching service categories:', error);
    throw error;
  }
};

/**
 * Fetch pages from WordPress
 */
export const fetchPages = async () => {
  try {
    const response = await wpApi.get(wpEndpoints.pages, {
      params: {
        per_page: 100,
        _embed: true,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching pages:', error);
    throw error;
  }
};

/**
 * Fetch a single page by ID or slug
 */
export const fetchPage = async (idOrSlug: string | number) => {
  try {
    const response = await wpApi.get(`${wpEndpoints.pages}/${idOrSlug}`, {
      params: {
        _embed: true,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching page:', error);
    throw error;
  }
};

/**
 * Search WordPress services
 * Uses WordPress REST API search parameter
 */
export const searchServices = async (query: string) => {
  try {
    if (!query || query.trim().length === 0) {
      // If empty query, return all services
      return await fetchServices();
    }
    
    const response = await wpApi.get(wpEndpoints.services, {
      params: {
        search: query.trim(),
        per_page: 100,
        _embed: true,
      },
    });
    
    // Handle case where response.data might not be an array
    let data = response.data;
    
    // If data is a string (might contain PHP output), try to parse JSON
    if (typeof data === 'string') {
      try {
        const jsonMatch = data.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          data = JSON.parse(jsonMatch[0]);
        }
      } catch (e) {
        console.error('Failed to parse JSON from string response:', e);
        return [];
      }
    }
    
    // Ensure we return an array
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error searching services:', error);
    throw error;
  }
};

/**
 * Search WordPress content (legacy - kept for backward compatibility)
 */
export const searchContent = async (query: string, type: 'service' | 'page' | 'post' = 'service') => {
  if (type === 'service') {
    return await searchServices(query);
  }
  
  try {
    const response = await wpApi.get(wpEndpoints.search, {
      params: {
        search: query,
        type: type,
        per_page: 20,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error searching content:', error);
    throw error;
  }
};

/**
 * Fetch media/attachments
 */
export const fetchMedia = async (mediaId: number) => {
  try {
    const response = await wpApi.get(`${wpEndpoints.media}/${mediaId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching media:', error);
    throw error;
  }
};

export default wpApi;

